import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import React, { createContext, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Dropdown, { type DropdownProps } from '@/components/Dropdown/Dropdown';
import SearchInput from '@/components/Inputs/SearchInput';
import Typo from '@/components/Typography/Typo';
import useAppLogout from '@/hooks/domain/App/use-app-logout';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useCommonStore from '@/stores/pure/CommonStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

const TRANSITION_DELAY = 200;

type WorkspaceDropdownProps = {} & DropdownProps;

const WorkspaceDropdownContext = createContext<{
  close?: () => void;
  initStoreWhenChangeWorkspace: () => void;
  initWholeStore: () => void;
}>({
  initStoreWhenChangeWorkspace: () => {},
  initWholeStore: () => {},
});

const WorkspaceDropdown = (props: WorkspaceDropdownProps) => {
  const { initWholeStore, initStoreWhenChangeWorkspace } = useCommonStore(
    (state) => ({
      initWholeStore: state.initWholeStore,
      initStoreWhenChangeWorkspace: state.initStoreWhenChangeWorkspace,
    }),
  );

  const { children, ...rest } = props;

  return (
    <Dropdown {...rest} style={{ display: 'flex', flexDirection: 'column' }}>
      <WorkspaceDropdownContext.Provider
        value={{
          close: props.close,
          initStoreWhenChangeWorkspace,
          initWholeStore,
        }}
      >
        <StyledWrapper>
          {
            // Between each React Children add Divider component except the last one
            React.Children.map(props.children, (child, index) => (
              <React.Fragment key={index}>
                {child}
                {index !== React.Children.count(props.children) - 1 && (
                  <Divider direction="h" />
                )}
              </React.Fragment>
            ))
          }
        </StyledWrapper>
      </WorkspaceDropdownContext.Provider>
    </Dropdown>
  );
};

const WorkspaceList = () => {
  const [searchValue, setSearchValue] = useState('');
  const { close: closeDropdown, initStoreWhenChangeWorkspace } = useContext(
    WorkspaceDropdownContext,
  );
  const { currWorkspace, workspaces } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
    workspaces: state.workspaces,
  }));

  const wr = useWorkspaceRouter();
  const queryClient = useQueryClient();

  const changeWorkspace = async (name: string) => {
    const cW = workspaces.find((w) => w.name === name);
    if (cW) {
      // Clear all query cache when change workspace
      queryClient.clear();
      closeDropdown?.();
      setTimeout(() => {
        initStoreWhenChangeWorkspace();
        wr.navigate(`/workspace/${cW.alias}`);
      }, TRANSITION_DELAY);
    }
  };

  const filteredWorkspaces = useMemo(() => {
    return workspaces.filter((w) =>
      w.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, workspaces]);

  return (
    <VFlex>
      <VFlex padding="4px 8px">
        <SearchInput
          style={{ flex: 1 }}
          hideBorder
          placeholder="Search workspace"
          value={searchValue}
          initFocus
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
      </VFlex>
      <Divider direction="h" />
      <VFlex
        style={{
          padding: '8px',
          maxHeight: 500,
          overflow: 'auto',
        }}
        f={1}
        g="2px"
        a="center"
      >
        {filteredWorkspaces
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name, alias }) => (
            <WorkspaceItem
              key={name}
              name={name}
              selected={alias === currWorkspace?.alias}
              onClick={changeWorkspace}
            />
          ))}
      </VFlex>
    </VFlex>
  );
};

const WorkspaceItem = ({
  name,
  selected,
  onClick,
}: {
  name: string;
  selected: boolean;
  onClick: (name: string) => void;
}) => {
  return (
    <StyledWorkspaceItemWrapper
      a="center"
      onClick={() => onClick(name)}
      className={selected ? 'selected' : ''}
    >
      {name}
    </StyledWorkspaceItemWrapper>
  );
};

const UserInfo = () => {
  const color = useColor();
  const { user } = useUserStore((state) => ({ user: state.user }));

  return (
    <StyledUserInfo>
      <Typo type="body3" weight="r" color={color.gray600}>
        {user?.email}
      </Typo>
    </StyledUserInfo>
  );
};

const CreateWorkspace = () => {
  const { close: closeDropdown, initStoreWhenChangeWorkspace } = useContext(
    WorkspaceDropdownContext,
  );

  const wr = useWorkspaceRouter();
  const toCreateWorkspace = async () => {
    wr.navigate('/create-workspace');
    initStoreWhenChangeWorkspace();
    closeDropdown?.();
  };

  return (
    <Button
      onClick={toCreateWorkspace}
      variant="ghost_secondary"
      leftIcon={<Plus />}
      sizeType="s"
      className="h-full w-full"
    >
      New workspace
    </Button>
  );
};

const LogOut = () => {
  const { close: closeDropdown } = useContext(WorkspaceDropdownContext);
  const logout = useAppLogout({ onAfterLocalLogout: closeDropdown });

  return (
    <Button
      style={{ margin: '0px 20px', border: 'none' }}
      variant="outlined"
      leftIcon={<LogOut />}
      onClick={logout}
    >
      Sign out
    </Button>
  );
};

WorkspaceDropdown.UserInfo = UserInfo;
WorkspaceDropdown.WorkspaceList = WorkspaceList;
WorkspaceDropdown.CreateWorkspace = CreateWorkspace;
WorkspaceDropdown.LogOut = LogOut;

const StyledWrapper = styled(VFlex)`
  flex: 1;
  overflow: auto;
`;

const StyledUserInfo = styled.div`
  padding: 12px 20px;
`;

const StyledWorkspaceItemWrapper = styled(Flex)`
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  transition: all 0.2s;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.colors.gray800};
  background-color: transparent;
  cursor: pointer;

  &.selected {
    background-color: ${(props) => props.theme.colors.selected};
    &:hover {
      background-color: ${(props) => props.theme.colors.selected};
    }
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
  }
`;

export default WorkspaceDropdown;
